/**
 * Handles storing/retrieving stashed carts locally and via the API.
 *
 * @constructor
 *
 * @param {CartStashHelper} cartStashHelperService
 * @param {ConnectivityChecker} connectivityChecker
 * @param stashedCartResource
 * @param {UserService} userService
 * @param {UserSessionManager} userSessionManager
 */
const CartStasher = function (
    cartStashHelperService,
    connectivityChecker,
    stashedCartResource,
    userService,
    userSessionManager
) {
    this.cartStashHelperService = cartStashHelperService;
    this.connectivityChecker = connectivityChecker;
    this.stashedCartResource = stashedCartResource;
    this.userService = userService;
    this.userSessionManager = userSessionManager;
};

CartStasher.prototype = {
    /**
     * @param {StashedCart} stashedCart
     *
     * @return {Promise<StashedCart>}
     */
    saveStashedCart(stashedCart) {
        return Promise
            .all([
                // Stash locally.
                this.userSessionManager
                    .getSession(this.userService.getUserID())
                    .then(async (userSession) => {
                        if (userSession) {
                            userSession.addStashedCart(stashedCart);
                            await this.userSessionManager.persist(userSession);
                        }
                    }),

                // Stash on API.
                this.storeRemoteStashedCart(stashedCart),
            ])
            .then(() => stashedCart);
    },

    /**
     * @return {Promise<StashedCart[]>}
     */
    getLocalStashedCarts: function getLocalStashedCarts() {
        return this.userSessionManager.getStashedCarts()
            .then((stashedCarts) => stashedCarts.map((stashedCart) => {
                stashedCart.stashSource = 'local';
                return stashedCart;
            }));
    },

    loadStashedCarts: function loadStashedCarts() {
        return new Promise((resolve) => {
            this.getLocalStashedCarts()
                .then((localStashedCarts) => {
                    if (!this.connectivityChecker.isOnline()) {
                        // Offline. Just return the locally stashed carts.
                        this.cartStashHelperService.sortStashedCarts(localStashedCarts);
                        resolve(localStashedCarts);
                        return;
                    }

                    this.getRemoteStashedCarts(
                        this.userService.currentBranch.ID,
                        async (remoteStashedCarts) => {
                            let remoteThirdPartyRefs = [];
                            for (let ri = 0; ri < remoteStashedCarts.length; ri++) {
                                //
                                remoteThirdPartyRefs.push(remoteStashedCarts[ri].thirdPartyRef);
                            }

                            // Merge local stashed carts into remote stashed carts.
                            for (let li = 0; li < localStashedCarts.length; li++) {
                                let localStash = localStashedCarts[li];

                                if (localStash.stashedCartID && remoteThirdPartyRefs.indexOf(localStash.thirdPartyRef) === -1) {
                                    // If the local stash has a stashedCartID, but it is not in the remote stashes,
                                    // that means it has been deleted on another device.
                                    // Delete the local stash
                                    await this.userSessionManager.deleteStashedCartByThirdPartyRef(localStash.thirdPartyRef);
                                } else if (remoteThirdPartyRefs.indexOf(localStash.thirdPartyRef) === -1) {
                                    // Skip showing local stashes already returned from remote stashes.
                                    remoteStashedCarts.push(localStash);
                                }
                            }

                            this.cartStashHelperService.sortStashedCarts(remoteStashedCarts);
                            resolve(remoteStashedCarts);
                        }
                    );
                });
        });
    },

    /**
     * @param {string} staffRef
     *
     * @return {Promise<StashedCart[]>}
     */
    findByStaffRef(staffRef) {
        staffRef = staffRef.toLowerCase();

        return this.loadStashedCarts()
            .then((stashedCarts) => {
                return stashedCarts.filter((sc) => sc.staffRef && sc.staffRef.toLowerCase() === staffRef);
            });
    },

    /**
     * Update the version of a stashed cart in the database.
     *
     * @param {StashedCart} stashedCart
     * @param {Function} [callback]
     *
     * @returns {Promise}
     */
    storeRemoteStashedCart: function storeRemoteStashedCart(stashedCart, callback) {
        if (!stashedCart.thirdPartyRef) {
            return Promise.reject();
        }

        let requestData = stashedCart.toServerData();
        requestData.requiresFulfillment = 0;

        return new Promise((resolve, reject) => {
            this.stashedCartResource.save(
                requestData,
                function (response) {
                    stashedCart.stashedCartID = response.stashedCartID;

                    if (callback) {
                        callback(stashedCart);
                    }

                    resolve();
                },
                () => {
                    reject();
                }
            );
        });
    },

    /**
     * Remove a stashed cart from the local and remote storage.
     *
     * @param {StashedCart} stashedCart
     */
    deleteStashedCart: function deleteStashedCart(stashedCart) {
        // Delete from the server if it exists there.
        this.deleteRemoteStashedCart(
            stashedCart,
            null
        );

        // Due to the nature of thirdPartyRef it should be safe to delete stashed carts from any user that
        // match the thirdPartyRef, as there should only be one.
        this.userSessionManager.deleteStashedCartByThirdPartyRef(stashedCart.thirdPartyRef);
    },

    /**
     * Delete a stashed cart from the database.
     *
     * @param {StashedCart} stashedCart
     * @param {function} [callback]
     */
    deleteRemoteStashedCart: function deleteRemoteStashedCart(stashedCart, callback) {
        return new Promise((resolve, reject) => {
            this.stashedCartResource.delete(
                {
                    thirdPartyRef: stashedCart.thirdPartyRef,
                },
                (response) => {
                    if (response.success) {
                        if (callback) {
                            callback(true);
                        }
                        resolve();
                    } else {
                        if (callback) {
                            callback(false);
                        }
                        reject();
                    }
                },
                () => {
                    if (callback) {
                        callback(false);
                    }
                    reject();
                }
            );
        });
    },

    getRemoteStashedCarts: function getRemoteStashedCarts(branchID, callback) {
        const self = this;
        this.stashedCartResource.query(
            {
                branchID,
                resolve: '*',
            },
            function (response) {
                let stashedCarts = [];
                for (let i = 0; i < response.length; i++) {
                    let stashedCart = self.cartStashHelperService.createStashedCartFromRemoteData(response[i]);
                    stashedCart.stashSource = 'remote';
                    stashedCarts.push(stashedCart);
                }

                callback(stashedCarts);
            }
        );
    },
};

module.exports = CartStasher;
